/**
 * Centralized axios instance with automatic Keycloak token injection
 * This eliminates the need to manually pass tokens in every API call
 */

import axios from 'axios';
import { kc } from '../keycloak';

// Create axios instance for authenticated API calls
const axiosAuth = axios.create();

// Request interceptor: Automatically inject the current Keycloak token
axiosAuth.interceptors.request.use(
  async (config) => {
    // Get the latest token from Keycloak instance
    if (kc.authenticated && kc.token) {
      config.headers.Authorization = `Bearer ${kc.token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor: Handle 401 errors by refreshing token
axiosAuth.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // If we get a 401 and haven't already tried to refresh
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      try {
        // Attempt to refresh the token
        const refreshed = await kc.updateToken(30);

        if (refreshed) {
          // Update the authorization header with the new token
          originalRequest.headers.Authorization = `Bearer ${kc.token}`;
          // Retry the original request
          return axiosAuth(originalRequest);
        } else {
          // Token refresh failed, redirect to login
          kc.logout();
          return Promise.reject(error);
        }
      } catch (refreshError) {
        // Token refresh failed, redirect to login
        kc.logout();
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  }
);

export default axiosAuth;
